import { IconsDropdown, useToasts } from '@brunommsilva/react-foundations';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CATEGORY_ICON_MAP, CATEGORY_ICONS } from '../../config/category-icons';
import { TEST_IDS } from '../../config/test-ids';
import useCreateCategory from '../../hooks/categories/use-create-category';

interface State {
  name: string;
  description?: string;
  icon?: string;
}

const INITIAL_STATE: State = {
  name: '',
  description: undefined,
  icon: undefined,
};

interface CreateCategoryFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isDialog?: boolean;
}

const CreateCategoryForm = ({
  onSuccess,
  onCancel,
  isDialog = false,
}: CreateCategoryFormProps) => {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const {
    createCategory,
    isCreating,
    wasCreatedWithSuccess,
    wasCreatedWithoutSuccess,
  } = useCreateCategory();

  useEffect(() => {
    if (wasCreatedWithSuccess) {
      showToast({
        title: t('CREATE_CATEGORY_SUCCESS_TOAST.TITLE'),
        message: t('CREATE_CATEGORY_SUCCESS_TOAST.MESSAGE'),
      });
      if (onSuccess) {
        onSuccess();
      }
    }
    if (wasCreatedWithoutSuccess) {
      showToast({
        title: t('CREATE_CATEGORY_ERROR_TOAST.TITLE'),
        message: t('CREATE_CATEGORY_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [
    wasCreatedWithSuccess,
    wasCreatedWithoutSuccess,
    onSuccess,
    t,
    showToast,
  ]);

  const isFormValid = () => {
    return state.name;
  };

  const onCreateAccount = () => {
    if (!isFormValid()) {
      return;
    }

    createCategory({
      name: state.name,
      description: state.description,
      icon: state.icon,
    });
  };

  const renderCreateButton = () => {
    return (
      <Button
        variant="contained"
        data-testid={TEST_IDS.categories.createButton}
        disabled={isCreating || !isFormValid()}
        onClick={onCreateAccount}
      >
        {t('CREATE')}
      </Button>
    );
  };

  const renderActions = () => {
    return (
      <Grid
        container
        sx={{
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {isDialog && onCancel && (
          <Button
            variant="outlined"
            color="inherit"
            disabled={isCreating}
            onClick={onCancel}
          >
            {t('CANCEL')}
          </Button>
        )}
        {renderCreateButton()}
      </Grid>
    );
  };

  const renderIconPicker = () => {
    const iconOptions = CATEGORY_ICONS.map((icon) => ({
      id: icon.id,
      label: t(icon.translationKey),
    }));

    return (
      <Box
        data-testid={TEST_IDS.categories.iconDropdown}
        sx={{ display: 'contents' }}
      >
        <IconsDropdown
          options={iconOptions}
          iconMap={CATEGORY_ICON_MAP}
          value={state.icon}
          onChange={(iconId) => setState({ ...state, icon: iconId })}
          label={t('CATEGORY_ICON')}
        />
      </Box>
    );
  };

  const formContent = (
    <Stack
      sx={{
        gap: 2,
        pt: isDialog ? 1 : 0,
      }}
    >
      <FormControl fullWidth>
        <TextField
          label={t('CATEGORY_NAME')}
          slotProps={{
            htmlInput: { 'data-testid': TEST_IDS.categories.nameInput },
          }}
          value={state.name}
          onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, name: change.target.value });
          }}
        />
      </FormControl>
      <FormControl fullWidth>
        <TextField
          label={t('CATEGORY_DESCRIPTION')}
          slotProps={{
            htmlInput: {
              'data-testid': TEST_IDS.categories.descriptionInput,
            },
          }}
          value={state.description || ''}
          onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, description: change.target.value });
          }}
        />
      </FormControl>
      {renderIconPicker()}
      <FormControl>{renderActions()}</FormControl>
    </Stack>
  );

  if (isDialog) {
    return formContent;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('CREATE_NEW_CATEGORY')}
          </Typography>
        }
      />
      <CardContent>{formContent}</CardContent>
    </Card>
  );
};

export default CreateCategoryForm;
